import React, { useEffect, useState, useRef } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert } from '@mui/material';
import { PlayArrow, Stop } from '@mui/icons-material';

const VideoPlayer = () => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [videoUrl, setVideoUrl] = useState('http://localhost:8081/live/.m3u8');
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState('');
    const [streamType, setStreamType] = useState('hls');
    const [isPlayerReady, setIsPlayerReady] = useState(false);

    const detectStreamType = (url) => {
        if (url.startsWith('rtmp://') || url.startsWith('rtmps://')) {
            return 'rtmp';
        } else if (url.includes('.m3u8')) {
            return 'hls';
        } else if (url.includes('.mpd')) {
            return 'dash';
        } else {
            return 'html5';
        }
    };


    // Inicializar quando a URL mudar
    useEffect(() => {
        if (!videoUrl) return;

        const initializeHLS = () => {
            if (!videoRef.current) {
                return;
            }

            const video = videoRef.current;
            const detectedType = detectStreamType(videoUrl);
            setStreamType(detectedType);

            // Limpar HLS anterior
            if (hlsRef.current) {
                try {
                    hlsRef.current.destroy();
                } catch (e) {
                    // Erro silencioso ao destruir HLS anterior
                }
                hlsRef.current = null;
            }

            if (detectedType === 'hls') {
                // Verificar se HLS.js está disponível
                if (typeof window.Hls === 'undefined') {
                    setError('HLS.js não foi carregado. Verifique a conexão com a internet.');
                    return;
                }

                if (window.Hls.isSupported()) {
                    const hls = new window.Hls({
                        enableWorker: true,
                        lowLatencyMode: true,
                        debug: false
                    });
                    
                    hlsRef.current = hls;
                    
                    // Event listeners essenciais
                    hls.on(window.Hls.Events.MANIFEST_PARSED, (event, data) => {
                        setIsPlayerReady(true);
                        setError('');
                    });

                    hls.on(window.Hls.Events.ERROR, (event, data) => {
                        if (data.fatal) {
                            switch (data.type) {
                                case window.Hls.ErrorTypes.NETWORK_ERROR:
                                    setError('Erro de rede: Não foi possível carregar o stream HLS');
                                    break;
                                case window.Hls.ErrorTypes.MEDIA_ERROR:
                                    setError('Erro de mídia: Problema com o formato do stream');
                                    break;
                                default:
                                    setError('Erro fatal no stream HLS');
                                    break;
                            }
                        }
                    });
                    hls.loadSource(videoUrl);
                    hls.attachMedia(video);
                    
                } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                    video.src = videoUrl;
                    setIsPlayerReady(true);
                    setError('');
                } else {
                    setError('HLS não é suportado neste navegador');
                }
            } else {
                video.src = videoUrl;
                setIsPlayerReady(true);
                setError('');
            }
        };

        // Aguardar um pouco para garantir que o elemento video esteja pronto
        const timer = setTimeout(() => {
            initializeHLS();
        }, 100);

        return () => clearTimeout(timer);
    }, [videoUrl]);

    // Cleanup ao desmontar
    useEffect(() => {
        return () => {
            if (hlsRef.current) {
                try {
                    hlsRef.current.destroy();
                } catch (e) {
                    // Erro silencioso ao destruir HLS
                }
            }
        };
    }, []);

    const handlePlay = () => {
        if (videoRef.current) {
            videoRef.current.play();
            setIsPlaying(true);
        }
    };

    const handleStop = () => {
        if (videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    };

    const handleUrlChange = (newUrl) => {
        setVideoUrl(newUrl);
        setIsPlayerReady(false);
        setIsPlaying(false);
    };





    return (
        <Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ mb: 2 }}>
                <TextField
                    label="URL do Stream/Vídeo"
                    value={videoUrl}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    fullWidth
                    variant="outlined"
                    placeholder="http://localhost:8081/live/.m3u8"
                    helperText={`Tipo detectado: ${streamType.toUpperCase()} | Suporta: RTMP, HLS, DASH, MP4, WebM`}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={handlePlay}
                    disabled={!videoUrl || isPlaying}
                >
                    {isPlaying ? 'Reproduzindo...' : 'Reproduzir'}
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<Stop />}
                    onClick={handleStop}
                    disabled={!isPlaying}
                >
                    Pausar
                </Button>
            </Box>

            <Box 
                sx={{ 
                    width: '100%', 
                    height: '420px',
                    backgroundColor: '#000',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    minHeight: '300px',
                    position: 'relative'
                }}
            >
                <video
                    ref={videoRef}
                    controls
                    muted
                    style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#000',
                        borderRadius: '4px'
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onError={(e) => {
                        setError('Erro ao reproduzir o vídeo');
                    }}
                />
                {!isPlayerReady && !error && (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1
                        }}
                    >
                        Carregando stream...
                    </Typography>
                )}
            </Box>

        </Paper>
    );
};

export default VideoPlayer;
